import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AipSmartCare.css";
import { fetchClients } from "../utils/api";
import PageHeader from "./layout/PageHeader";
import { calculateAipExcel } from "../utils/aipExcelModel";
import { Brain } from "lucide-react";

const AipSmartCare = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const loadClients = async () => {
    setError("");
    setLoading(true);
    try {
      const locId = activeLocation?.id || activeLocation?._id || null;
      const companyId =
        currentUser?.companyId ||
        currentUser?.company?._id ||
        activeLocation?.companyId ||
        locations?.[0]?.companyId ||
        null;

      if (!locId && !companyId) {
        setResults([]);
        setHasSearched(true);
        setError("Select a location or company to search clients.");
        return;
      }

      const term = searchTerm.trim();
      let clients = await fetchClients({
        locationId: locId,
        companyId,
        searchTerm: term,
      });

      // Fallback: if location-scoped search returns nothing but company exists,
      // retry company-wide with the same search term (handles HQ with no folders).
      if (clients?.length === 0 && companyId) {
        try {
          const companyWide = await fetchClients({
            companyId,
            searchTerm: term,
          });
          if (companyWide?.length) {
            clients = companyWide;
          }
        } catch (e) {
          console.warn("Company-wide fallback failed", e);
        }
      }

      setResults(clients);
      setHasSearched(true);
    } catch (err) {
      setError("Failed to search clients. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Load on first render and when location changes
  useEffect(() => {
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocation]);

  // Dynamic search with debounce, hitting backend
  useEffect(() => {
    const id = setTimeout(() => {
      loadClients();
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openAnalysis = (id) => {
    // ✅ Navigate to MODELING MODE path (context-aware from AIP Smart Care)
    navigate(`/aip-smart-care/client/${id}`);
  };

  const scoreBand = (client) => {
    // Prefer persisted AIP score from backend; fall back to calculated if missing
    const persistedAip =
      client?.aipScore !== undefined && client?.aipScore !== null
        ? Number(client.aipScore)
        : null;

    let value = Number.isFinite(persistedAip) ? persistedAip : null;
    let label = null;

    if (value === null) {
      const aip = calculateAipExcel({
        FWB: client.fwbScore ?? client.wellBeingScore,
        CT: client.careTeamScore,
        ES: client.environmentScore,
        lambda: client.lambda,
        gamma: client.gamma,
        sigma: client.sigma,
        kappa: client.kappa,
      });
      value = aip?.score ?? null;
      label = aip?.label ?? null;
    }

    if (value === null)
      return { cls: "score-red", label: "Needs Data", value: "--" };
    if (value >= 75)
      return { cls: "score-green", label: label || "Good", value };
    if (value >= 40)
      return { cls: "score-orange", label: label || "Moderate", value };
    return { cls: "score-red", label: label || "At Risk", value };
  };

  const getDisplayClientId = (client) => client?.clientId || "—";
  const isAutoProvisional = (client) =>
    client?.isAipProvisional === true ||
    String(client?.latestAssessmentStatus || "").toLowerCase() ===
      "auto-provisional";

  return (
    <div className="aip-smart-care-container page-shell">
      <PageHeader
        title="AIP Smart Care"
        subtitle="Search clients and jump straight into AIP analysis."
        icon={<Brain className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      <div className="page-actions">
        <button
          className="asc-refresh"
          onClick={loadClients}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <form
        className="asc-search-bar"
        onSubmit={(e) => {
          e.preventDefault();
          loadClients();
        }}
      >
        <div className="asc-input-wrapper">
          <span className="asc-input-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by Client Name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button type="submit" disabled={loading}>
          {loading ? "Searching..." : "Search"}
        </button>
      </form>

      {error && <div className="asc-error">{error}</div>}

      <div className="asc-results">
        {loading && <div className="asc-status">Loading results...</div>}
        {!loading && hasSearched && results.length === 0 && (
          <div className="asc-status">No clients found.</div>
        )}
        {!loading &&
          results.map((c) => {
            const band = scoreBand(c);
            const provisional = isAutoProvisional(c);
            return (
              <div className="asc-card" key={c.id || c._id}>
                <div className="asc-card-name">
                  {c.firstName} {c.lastName}
                </div>
                <div className="asc-card-id">
                  Client ID: {getDisplayClientId(c)}
                </div>
                <div className={`asc-card-score ${band.cls}`}>
                  AIP Score: {band.value ?? "--"}{" "}
                  <span className="asc-pill">{band.label}</span>
                  {provisional ? (
                    <span
                      className="asc-provisional-tag"
                      title="Based on WellSky care plan only"
                    >
                      Provisional
                    </span>
                  ) : null}
                </div>
                <button onClick={() => openAnalysis(c.id || c._id)}>
                  Open AIP Analysis
                </button>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default AipSmartCare;
